import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Plus, Pencil, Trash2, Upload, Store, Package, Download, FileUp, FileText, Images } from 'lucide-react';
import axios from 'axios';
import { PLACEHOLDER_IMG, handleImgError } from '@/lib/imagePlaceholder';

const API = window.location.origin + '/api';

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [supermarkets, setSupermarkets] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [form, setForm] = useState({ name: '', price: '', category_id: '', supermarket_id: '', description: '', image_url: '', unit: '', in_stock: true, discount_percent: 0, is_weighted: false, weight_type: '', price_per_kg: '' });
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [showBulkImages, setShowBulkImages] = useState(false);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const importFileRef = useRef(null);

  const fetchData = useCallback(async () => {
    const [p, c, s] = await Promise.all([
      axios.get(`${API}/admin/products`, { withCredentials: true }),
      axios.get(`${API}/admin/categories`, { withCredentials: true }),
      axios.get(`${API}/admin/supermarkets`, { withCredentials: true })
    ]);
    setProducts(p.data);
    setCategories(c.data);
    setSupermarkets(s.data);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Store filter derived directly from URL params (no duplicated state)
  const activeStore = searchParams.get('store') || 'all';

  const filteredProducts = useMemo(() => (
    activeStore === 'all' ? products : products.filter(p => p.supermarket_slug === activeStore)
  ), [activeStore, products]);

  const handleStoreTab = (slug) => {
    if (slug === 'all') {
      searchParams.delete('store');
    } else {
      searchParams.set('store', slug);
    }
    setSearchParams(searchParams);
  };

  const getActiveStoreName = () => {
    if (activeStore === 'all') return 'All Stores';
    return supermarkets.find(s => s.slug === activeStore)?.name || activeStore;
  };

  const getActiveStoreId = () => {
    if (activeStore === 'all') return '';
    return supermarkets.find(s => s.slug === activeStore)?.id || '';
  };

  const openCreate = () => {
    setEditProduct(null);
    setForm({
      name: '', price: '', category_id: categories[0]?.id || '',
      supermarket_id: getActiveStoreId(),
      description: '', image_url: '', unit: '', in_stock: true, discount_percent: 0,
      is_weighted: false, weight_type: '', price_per_kg: ''
    });
    setShowModal(true);
  };

  const openEdit = (p) => {
    setEditProduct(p);
    setForm({
      name: p.name, price: p.price, category_id: p.category_id,
      supermarket_id: p.supermarket_id || '',
      description: p.description || '', image_url: p.image_url || '', unit: p.unit || '',
      in_stock: p.in_stock, discount_percent: p.discount_percent || 0,
      is_weighted: p.is_weighted || false, weight_type: p.weight_type || '',
      price_per_kg: p.price_per_kg || ''
    });
    setShowModal(true);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { alert('Image must be under 5MB'); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await axios.post(`${API}/upload`, formData, {
        withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' }
      });
      setForm(prev => ({ ...prev, image_url: data.url }));
    } catch (err) {
      alert('Upload failed: ' + (err.response?.data?.detail || err.message));
    }
    setUploading(false);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    const payload = {
      ...form, price: parseFloat(form.price), discount_percent: parseFloat(form.discount_percent) || 0,
      price_per_kg: form.is_weighted ? parseFloat(form.price_per_kg) || 0 : 0,
      weight_type: form.is_weighted ? form.weight_type : ''
    };
    try {
      if (editProduct) {
        await axios.put(`${API}/admin/products/${editProduct.id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API}/admin/products`, payload, { withCredentials: true });
      }
      setShowModal(false);
      fetchData();
    } catch (err) {
      alert('Save failed: ' + (err.response?.data?.detail || err.message));
    }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    await axios.delete(`${API}/admin/products/${id}`, { withCredentials: true });
    fetchData();
  };

  // --- Bulk Import/Export ---
  const handleExport = () => {
    const params = activeStore !== 'all' ? `?supermarket=${activeStore}` : '';
    window.open(`${API}/admin/products/export${params}`, '_blank');
  };

  const handleDownloadTemplate = () => {
    window.open(`${API}/admin/products/template`, '_blank');
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const params = activeStore !== 'all' ? `?supermarket_slug=${activeStore}` : '';
      const { data } = await axios.post(`${API}/admin/products/import${params}`, formData, {
        withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' }
      });
      setImportResult(data);
      fetchData();
    } catch (err) {
      setImportResult({ message: err.response?.data?.detail || 'Import failed', imported: 0, errors: [] });
    }
    setImporting(false);
    if (importFileRef.current) importFileRef.current.value = '';
  };

  const handleBulkImages = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setBulkUploading(true);
    setBulkResult(null);
    try {
      const formData = new FormData();
      files.forEach(f => formData.append('files', f));
      const params = activeStore !== 'all' ? `?supermarket_slug=${activeStore}` : '';
      const { data } = await axios.post(`${API}/admin/products/bulk-images${params}`, formData, {
        withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' }
      });
      setBulkResult(data);
      fetchData();
    } catch (err) {
      setBulkResult({ message: err.response?.data?.detail || 'Bulk upload failed', matched: [], unmatched: [] });
    }
    setBulkUploading(false);
    e.target.value = '';
  };

  const storeColor = (slug) => supermarkets.find(s => s.slug === slug)?.color || 'var(--navy)';

  return (
    <div className="admin-page" data-testid="admin-products-page">
      <button className="back-btn" onClick={() => navigate('/admin')}>
        <ArrowLeft /> Dashboard
      </button>

      <h2 style={{ margin: '0 0 16px' }}>Product Catalogs</h2>

      {/* Supermarket Tabs */}
      <div className="store-tabs" data-testid="store-tabs">
        <button
          className={`store-tab ${activeStore === 'all' ? 'store-tab-active' : ''}`}
          onClick={() => handleStoreTab('all')}
          data-testid="tab-all"
        >
          <Package size={16} />
          All ({products.length})
        </button>
        {supermarkets.map(s => {
          const count = products.filter(p => p.supermarket_slug === s.slug).length;
          return (
            <button
              key={s.slug}
              className={`store-tab ${activeStore === s.slug ? 'store-tab-active' : ''}`}
              onClick={() => handleStoreTab(s.slug)}
              style={activeStore === s.slug ? { borderColor: s.color, color: s.color } : {}}
              data-testid={`tab-${s.slug}`}
            >
              {s.logo_url ? (
                <img src={s.logo_url} alt="" style={{ width: 20, height: 20, borderRadius: 4, objectFit: 'cover' }} />
              ) : (
                <Store size={16} />
              )}
              {s.name} ({count})
            </button>
          );
        })}
      </div>

      {/* Active store indicator + Add button */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
        <div>
          <span style={{ fontSize: '0.9rem', color: 'var(--text-muted)' }}>
            Showing: <strong style={{ color: 'var(--navy)' }}>{getActiveStoreName()}</strong>
            {activeStore !== 'all' && <span> &mdash; {filteredProducts.length} products</span>}
          </span>
        </div>
        <button className="btn-lime btn-small" onClick={openCreate} data-testid="add-product-btn"
          style={{ display: 'flex', alignItems: 'center', gap: '6px', width: 'auto', padding: '8px 16px' }}>
          <Plus size={16} /> Add Product {activeStore !== 'all' ? `to ${getActiveStoreName()}` : ''}
        </button>
      </div>

      {/* Import/Export Toolbar */}
      <div className="import-export-bar" data-testid="import-export-bar">
        <button onClick={handleExport} className="ie-btn" data-testid="export-btn">
          <Download size={15} /> Export CSV
        </button>
        <button onClick={handleDownloadTemplate} className="ie-btn" data-testid="template-btn">
          <FileText size={15} /> Download Template
        </button>
        <label className="ie-btn ie-btn-import" data-testid="import-label">
          <FileUp size={15} /> {importing ? 'Importing...' : 'Import CSV'}
          <input type="file" accept=".csv" onChange={handleImport} ref={importFileRef} style={{ display: 'none' }} disabled={importing} data-testid="import-input" />
        </label>
        <button onClick={() => { setShowBulkImages(true); setBulkResult(null); }} className="ie-btn" data-testid="bulk-images-btn">
          <Images size={15} /> Bulk Images
        </button>
      </div>

      {/* Import Result */}
      {importResult && (
        <div style={{ padding: '12px 16px', borderRadius: '10px', marginBottom: '16px', background: importResult.imported > 0 ? '#D1FAE5' : '#FEF2F2', color: importResult.imported > 0 ? '#065F46' : '#991B1B', fontSize: '0.85rem' }} data-testid="import-result">
          <strong>{importResult.message}</strong>
          {importResult.errors && importResult.errors.length > 0 && (
            <ul style={{ margin: '8px 0 0', paddingLeft: '18px' }}>
              {importResult.errors.slice(0, 5).map((err, i) => <li key={`import-err-${i}-${err.slice(0, 20)}`}>{err}</li>)}
              {importResult.errors.length > 5 && <li>...and {importResult.errors.length - 5} more</li>}
            </ul>
          )}
        </div>
      )}

      {/* Products Table */}
      <div style={{ overflowX: 'auto' }}>
        <table className="admin-table" data-testid="products-table">
          <thead>
            <tr>
              <th>Image</th><th>Name</th><th>Price (PGK)</th><th>Category</th>
              {activeStore === 'all' && <th>Store</th>}
              <th>Stock</th><th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredProducts.length === 0 ? (
              <tr><td colSpan={activeStore === 'all' ? 7 : 6} style={{ textAlign: 'center', padding: '24px', color: 'var(--text-muted)' }}>
                No products{activeStore !== 'all' ? ` in ${getActiveStoreName()}` : ''}. Click "Add Product" to create one.
              </td></tr>
            ) : (
              filteredProducts.map(p => (
                <tr key={p.id} data-testid={`product-row-${p.id}`}>
                  <td><img src={p.image_url || PLACEHOLDER_IMG} alt="" onError={handleImgError} style={{ width: 40, height: 40, borderRadius: 6, objectFit: 'cover' }} /></td>
                  <td>{p.name}</td>
                  <td>
                    {p.price.toFixed(2)}
                    {p.discount_percent > 0 && <span style={{ color: 'var(--danger)', fontSize: '0.75rem', marginLeft: 4 }}>-{p.discount_percent}%</span>}
                  </td>
                  <td>{p.category_name}</td>
                  {activeStore === 'all' && (
                    <td>
                      <span style={{
                        display: 'inline-block', padding: '2px 8px', borderRadius: '6px', fontSize: '0.75rem',
                        fontWeight: 600, background: p.supermarket_slug ? `${storeColor(p.supermarket_slug)}15` : '#f3f4f6',
                        color: p.supermarket_slug ? storeColor(p.supermarket_slug) : 'var(--text-muted)'
                      }}>
                        {p.supermarket_name || 'Unassigned'}
                      </span>
                    </td>
                  )}
                  <td>{p.in_stock ? 'Yes' : 'No'}</td>
                  <td>
                    <div className="admin-actions">
                      <button className="btn-edit" onClick={() => openEdit(p)} data-testid={`edit-btn-${p.id}`}><Pencil size={14} /></button>
                      <button className="btn-delete" onClick={() => handleDelete(p.id)} data-testid={`delete-btn-${p.id}`}><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Bulk Image Upload Modal */}
      {showBulkImages && (
        <div className="modal-overlay" onClick={() => setShowBulkImages(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} data-testid="bulk-images-modal">
            <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Images size={20} /> Bulk Image Upload</h3>
            <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', margin: '8px 0 4px' }}>
              Upload many product photos at once. Each file is matched to a product by its <strong>filename</strong> —
              e.g. <code>broccoli.jpg</code> updates the product named "Broccoli".
            </p>
            <p style={{ fontSize: '0.8rem', color: 'var(--navy)', fontWeight: 600, margin: '0 0 12px' }}>
              Matching scope: {getActiveStoreName()}
            </p>
            <label htmlFor="bulk-images-input" data-testid="bulk-images-label" style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
              padding: '24px', border: '2px dashed var(--border-gray)', borderRadius: '10px',
              cursor: bulkUploading ? 'not-allowed' : 'pointer', background: 'var(--light-gray)',
              color: 'var(--text-muted)', fontSize: '0.9rem', marginBottom: '12px'
            }}>
              <Upload size={20} /> {bulkUploading ? 'Uploading & matching...' : 'Select images (multiple)'}
            </label>
            <input id="bulk-images-input" type="file" accept="image/*" multiple onChange={handleBulkImages}
              style={{ display: 'none' }} disabled={bulkUploading} data-testid="bulk-images-input" />

            {bulkResult && (
              <div style={{ fontSize: '0.85rem' }} data-testid="bulk-images-result">
                <div style={{ padding: '10px 14px', borderRadius: '10px', marginBottom: '10px', background: (bulkResult.matched?.length || 0) > 0 ? '#D1FAE5' : '#FEF2F2', color: (bulkResult.matched?.length || 0) > 0 ? '#065F46' : '#991B1B', fontWeight: 600 }}>
                  {bulkResult.message}
                </div>
                {bulkResult.matched?.length > 0 && (
                  <ul style={{ margin: '0 0 10px', paddingLeft: '18px', color: '#065F46' }}>
                    {bulkResult.matched.map(m => (
                      <li key={m.file}>{m.file} &rarr; {m.products.join(', ')}</li>
                    ))}
                  </ul>
                )}
                {bulkResult.unmatched?.length > 0 && (
                  <ul style={{ margin: 0, paddingLeft: '18px', color: '#991B1B' }}>
                    {bulkResult.unmatched.map(u => (
                      <li key={u.file}>{u.file} — {u.reason}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            <button type="button" className="btn-primary" onClick={() => setShowBulkImages(false)} style={{ marginTop: '12px' }} data-testid="bulk-images-close-btn">
              Done
            </button>
          </div>
        </div>
      )}

      {/* Add/Edit Product Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} data-testid="product-modal">
            <h3>{editProduct ? 'Edit Product' : 'Add Product'}</h3>
            <form onSubmit={handleSave}>
              {/* Supermarket Assignment */}
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Store size={16} style={{ color: 'var(--navy)' }} /> Supermarket
                </label>
                <select
                  value={form.supermarket_id}
                  onChange={e => setForm({...form, supermarket_id: e.target.value})}
                  required
                  data-testid="product-supermarket-select"
                  style={{ fontWeight: 500 }}
                >
                  <option value="">-- Select Supermarket --</option>
                  {supermarkets.map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Name</label>
                <input value={form.name} onChange={e => setForm({...form, name: e.target.value})} required data-testid="product-name-input" />
              </div>
              <div className="form-group">
                <label>Price (PGK)</label>
                <input type="number" step="0.01" value={form.price} onChange={e => setForm({...form, price: e.target.value})} required data-testid="product-price-input" />
              </div>
              <div className="form-group">
                <label>Category</label>
                <select value={form.category_id} onChange={e => setForm({...form, category_id: e.target.value})} required data-testid="product-category-select">
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>

              {/* Image Upload */}
              <div className="form-group">
                <label>Product Image</label>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <label htmlFor="image-upload" data-testid="image-upload-label" style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                      padding: '12px', border: '2px dashed var(--border-gray)', borderRadius: '10px',
                      cursor: 'pointer', background: 'var(--light-gray)', color: 'var(--text-muted)', fontSize: '0.85rem'
                    }}>
                      <Upload size={18} /> {uploading ? 'Uploading...' : 'Upload Image'}
                    </label>
                    <input id="image-upload" type="file" accept="image/*" onChange={handleImageUpload}
                      style={{ display: 'none' }} disabled={uploading} data-testid="image-upload-input" />
                    <input value={form.image_url} onChange={e => setForm({...form, image_url: e.target.value})}
                      placeholder="Or paste image URL" style={{ marginTop: '8px' }} data-testid="product-image-input" />
                  </div>
                  {form.image_url && (
                    <img src={form.image_url} alt="Preview" data-testid="image-preview"
                      style={{ width: 80, height: 80, borderRadius: 10, objectFit: 'cover', border: '1px solid var(--border-gray)' }}
                      onError={(e) => { e.target.style.display = 'none'; }} />
                  )}
                </div>
              </div>

              <div className="form-group">
                <label>Unit</label>
                <input value={form.unit} onChange={e => setForm({...form, unit: e.target.value})} placeholder="kg, pack, bottle..." data-testid="product-unit-input" />
              </div>
              <div className="form-group">
                <label>Discount %</label>
                <input type="number" value={form.discount_percent} onChange={e => setForm({...form, discount_percent: e.target.value})} data-testid="product-discount-input" />
              </div>

              {/* Weighted Product Config */}
              <div style={{ padding: '12px 14px', borderRadius: '10px', border: '1px solid var(--border-gray)', marginBottom: '16px', background: form.is_weighted ? '#FFFBEB' : 'transparent' }}>
                <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: form.is_weighted ? '12px' : 0 }}>
                  <input type="checkbox" checked={form.is_weighted} onChange={e => setForm({...form, is_weighted: e.target.checked})} data-testid="product-weighted-checkbox" />
                  <label style={{ margin: 0, fontWeight: 600 }}>Weighted Product (sold by kg)</label>
                </div>
                {form.is_weighted && (
                  <>
                    <div className="form-group">
                      <label>Weight Type</label>
                      <select value={form.weight_type} onChange={e => setForm({...form, weight_type: e.target.value})} required data-testid="product-weight-type">
                        <option value="">Select type...</option>
                        <option value="meat">Meat (K10 start, K4 intervals)</option>
                        <option value="produce">Fresh Produce (K5 start, K5 intervals)</option>
                      </select>
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label>Price per Kg (PGK)</label>
                      <input type="number" step="0.01" value={form.price_per_kg} onChange={e => setForm({...form, price_per_kg: e.target.value})} placeholder="e.g. 18.00" data-testid="product-price-per-kg" />
                    </div>
                    <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '6px' }}>
                      {form.weight_type === 'meat' ? 'Customers will see: K10-K14, K14-K18, K18-K22... with piece estimates' : 'Customers will see: K5-K10, K10-K15, K15-K20...'}
                    </p>
                  </>
                )}
              </div>

              <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input type="checkbox" checked={form.in_stock} onChange={e => setForm({...form, in_stock: e.target.checked})} data-testid="product-stock-checkbox" />
                <label style={{ margin: 0 }}>In Stock</label>
              </div>
              <button type="submit" className="btn-primary" disabled={loading} data-testid="save-product-btn">
                {loading ? 'Saving...' : editProduct ? 'Update Product' : 'Add Product'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
